import { AxiosError } from 'axios'
import { Routes } from '@/configs'
import { AlertType } from '../alert/alert.styles'
import { ErrorResponse } from '@shared/models'

export class ApiError extends Error {
  public readonly statusCode: number
  public readonly backendMessage?: string
  public readonly errors?: ErrorResponse['errors']

  constructor(message: string, statusCode: number, backendMessage?: string, errors?: ErrorResponse['errors']) {
    super(message)
    this.name = 'ApiError'
    this.statusCode = statusCode
    this.backendMessage = backendMessage
    this.errors = errors
  }

  /**
   * Retorna a mensagem do backend se disponível, caso contrário retorna a mensagem genérica
   */
  getDisplayMessage(): string {
    return this.backendMessage || this.message
  }
}

function formatErrorsList(errors: ErrorResponse['errors']): string {
  if (!errors?.length) return ''

  return errors
    .map((err) => {
      if (typeof err === 'string') return `• ${err}`
      return err.field ? `• ${err.field}: ${err.message}` : `• ${err.message}`
    })
    .join('\n')
}

export interface ErrorHandlerDependencies {
  sendAlert: (alert: { type: AlertType; title: string; message: string }) => void
  unAuthorize: () => void
  navigate: (path: string) => void
}

export class ApiErrorHandler {
  private dependencies: ErrorHandlerDependencies
  private hasShownAuthError = false
  private authErrorTimeout: NodeJS.Timeout | null = null

  constructor(dependencies: ErrorHandlerDependencies) {
    this.dependencies = dependencies
  }

  private resetAuthErrorFlagInternal = () => {
    this.hasShownAuthError = false
    if (this.authErrorTimeout) {
      clearTimeout(this.authErrorTimeout)
      this.authErrorTimeout = null
    }
  }

  private handleAuthError = () => {
    if (this.hasShownAuthError) return

    this.hasShownAuthError = true
    this.dependencies.sendAlert({
      type: 'error',
      title: 'Sessão expirada',
      message: 'Sua sessão expirou. Você será redirecionado para o login.',
    })

    this.dependencies.unAuthorize()
    this.authErrorTimeout = setTimeout(this.resetAuthErrorFlagInternal, 2000)
  }

  /**
   * Quando a requisição usa `responseType: 'blob'` (downloads), o corpo de erro
   * também chega como Blob — sem desempacotar, a mensagem real do backend se
   * perde e o alerta cai no texto genérico do status.
   */
  private async parseErrorData(error: AxiosError): Promise<ErrorResponse | undefined> {
    const data = error.response?.data
    if (!data) return undefined

    if (data instanceof Blob) {
      try {
        return JSON.parse(await data.text()) as ErrorResponse
      } catch {
        return undefined
      }
    }

    return data as ErrorResponse
  }

  handleError = async (error: AxiosError): Promise<never> => {
    // Extrair mensagem do backend se disponível
    const errorData = await this.parseErrorData(error)
    const backendMessage = errorData?.message

    if (!error.response) {
      this.dependencies.sendAlert({
        type: 'error',
        title: 'Erro de conexão',
        message: 'Não foi possível conectar ao servidor. Verifique sua conexão.',
      })
      return Promise.reject(new ApiError('Erro de conexão', 0))
    }

    const status = error.response.status
    let alertTitle = 'Erro'
    let alertMessage = 'Ocorreu um erro'

    switch (status) {
      case 400:
        alertTitle = 'Erro de requisição'
        alertMessage = backendMessage || 'A requisição enviada é inválida, verifique-a e tente novamente!'
        break

      case 401:
        this.handleAuthError()
        return Promise.reject(new ApiError('Não autorizado', status, backendMessage))

      case 403:
        alertTitle = 'Erro de autorização'
        alertMessage = backendMessage || 'Você não possui permissões para acessar esse recurso!'
        this.dependencies.navigate(Routes.Home.path)
        break

      case 404:
        alertTitle = 'Recurso não encontrado'
        alertMessage =
          backendMessage || 'Ocorreu um erro ao buscar o recurso solicitado, verifique a requisição e tente novamente'
        this.dependencies.navigate(Routes.Home.path)
        break

      case 409:
        alertTitle = 'Recurso já existe'
        alertMessage =
          backendMessage ||
          'Não foi possível concluir a solicitação porque o recurso já existe. ' +
            'Por favor, revise as informações e tente novamente'
        break

      case 422:
        alertTitle = 'Erro de validação'
        alertMessage = backendMessage || 'Dados inválidos fornecidos.'
        break

      default:
        alertTitle = 'Erro inesperado'
        alertMessage =
          backendMessage ||
          'Ocorreu um erro inesperado ao efetuar a requisição, tente novamente mais tarde ou contate o suporte!'
        break
    }

    const errorsList = formatErrorsList(errorData?.errors)
    if (errorsList) alertMessage = `${alertMessage}\n${errorsList}`

    this.dependencies.sendAlert({
      type: 'error',
      title: alertTitle,
      message: alertMessage,
    })

    return Promise.reject(new ApiError(alertMessage, status, backendMessage, errorData?.errors))
  }

  resetAuthErrorFlag() {
    this.resetAuthErrorFlagInternal()
  }
}
